import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QrReader } from "react-qr-reader";
import ApiURl from "../Paths/Api";

export default function ScanPage() {
  const [qrData, setQrData] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [meetingData, setMeetingData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [iconPosition, setIconPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 80 });
  const [scanStage, setScanStage] = useState(1);
  const [firstScanData, setFirstScanData] = useState(null);
  const [attendeeInfo, setAttendeeInfo] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const navigate = useNavigate();
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isClick = useRef(true);

  // Camera constraints
  const isSmallScreen = window.innerWidth <= 768;
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileDevice = /iphone|ipad|ipod|android/.test(userAgent);

  const cameraConstraints = (() => {
    if (isMobileDevice || isSmallScreen) {
      console.log("[cameraConstraints] Mobile or small screen detected - BACK camera enforced");
      return {
        facingMode: { exact: "environment" },
      };
    }

    console.log("[cameraConstraints] Desktop/large screen detected - FRONT camera");
    return {
      facingMode: "user",
    };
  })();

  const fetchStatus = async () => {
    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        const error = new Error("Phone number not found in local storage");
        console.error("[fetchStatus] Error:", error.message);
        throw error;
      }

      console.log("[fetchStatus] Sending request to get_meetings.php", { phone });
      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        const error = new Error(`HTTP error: ${response.status} ${response.statusText}`);
        console.error("[fetchStatus] HTTP Error:", error.message, { status: response.status });
        throw error;
      }

      const data = await response.json();
      console.log("[fetchStatus] API Response:", data);

      if (data.success && data.data && data.data.length > 0) {
        const meeting = data.data[0];
        const meetingData = {
          purpose: meeting.purpose || "Unknown",
          location: meeting.location || "Unknown",
          time: meeting.time || new Date().toISOString(),
          status: meeting.status || "Unknown",
          description: meeting.description || "No description provided",
          attendees: Array.isArray(meeting.attendees) ? meeting.attendees : [],
        };
        console.log("[fetchStatus] Setting meetingData:", meetingData);
        setMeetingData(meetingData);
      } else {
        console.error("[fetchStatus] No meetings found:", data.message || "Empty data");
        setMeetingData(null);
      }
    } catch (err) {
      console.error("[fetchStatus] Error:", err.message, { stack: err.stack });
      setMeetingData(null);
    }
  };

  const handleFirstScan = async (scannedData) => {
    setIsLoading(true);
    try {
      console.log("[handleFirstScan] Processing first scan:", scannedData);

      const response = await fetch(`${ApiURl}/first_scan.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unique_id: scannedData }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      console.log("[handleFirstScan] API Response:", data);

      if (data.success) {
        setFirstScanData(scannedData);
        setAttendeeInfo({
          name: data.attendee_name,
          email: data.attendee_email,
          phone: data.attendee_phone,
          attendee_type: data.attendee_type || "Guest",
          meeting_purpose: data.meeting_purpose,
          // Add branch data from your form if available
          branch: data.branch || data.department || data.company || "Main"
        });
        setScanStage(2);
        alert(`First scan successful!\nAttendee: ${data.attendee_name}\nType: ${data.attendee_type || 'Guest'}\n\nPlease scan the same QR code again to verify attendance.`);
      } else {
        alert("Error: " + data.message);
        setIsCameraOpen(true);
      }
    } catch (err) {
      console.error("[handleFirstScan] Error:", err.message);
      alert("Error processing scan: " + err.message);
      setIsCameraOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecondScan = async (scannedData) => {
    if (scannedData !== firstScanData) {
      alert("QR codes don't match! Please scan the same QR code again.");
      setIsCameraOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      console.log("[handleSecondScan] Verifying second scan:", scannedData);

      const phone = localStorage.getItem('phone');
      if (!phone) {
        throw new Error("Phone number not found");
      }

      const response = await fetch(`${ApiURl}/verify.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unique_id: scannedData,
          phone: phone,
          verification_stage: "second_scan"
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      console.log("[handleSecondScan] Verification response:", data);

      if (data.success) {
        setIsVerified(true);
        setIsButtonDisabled(true);
        setEmailSent(true);

        setTicketData({
          attendee_name: attendeeInfo.name,
          attendee_type: attendeeInfo.attendee_type,
          meeting_purpose: attendeeInfo.meeting_purpose,
          verification_time: new Date().toLocaleString(),
          ticket_id: data.ticket_id || `TICK${Date.now()}`,
          unique_id: scannedData
        });

        alert("Verification successful! Ticket has been generated.");
      } else {
        alert("Verification failed: " + data.message);
        setIsCameraOpen(true);
      }
    } catch (err) {
      console.error("[handleSecondScan] Error:", err.message);
      alert("Error during verification: " + err.message);
      setIsCameraOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanResult = (result) => {
    if (!result?.text) return;

    console.log("[handleScanResult] Scanned QR:", result.text);
    setQrData(result.text);
    setIsCameraOpen(false);

    if (scanStage === 1) {
      handleFirstScan(result.text);
    } else if (scanStage === 2) {
      handleSecondScan(result.text);
    }
  };

  const handleVerify = async () => {
    if (!qrData) {
      console.error("[handleVerify] No QR data available");
      return;
    }

    if (scanStage === 1) {
      handleFirstScan(qrData);
    } else if (scanStage === 2) {
      handleSecondScan(qrData);
    }
  };

  const handleBack = () => {
    console.log("[handleBack] Navigating back");
    navigate(-1);
  };

  const handleNewScan = () => {
    console.log("[handleNewScan] Resetting scan state");
    setQrData(null);
    setFirstScanData(null);
    setAttendeeInfo(null);
    setTicketData(null);
    setIsVerified(false);
    setIsButtonDisabled(false);
    setEmailSent(false);
    setScanStage(1);
  };

  const handlePrintTicket = () => {
    if (!ticketData || !attendeeInfo) return;

    const printWindow = window.open('', '_blank');

    // Card dimensions in millimeters (111mm × 155mm)
    const cardWidth = 111;
    const cardHeight = 155;

    // Exact measurements from your card
    const vacantAreaTop = 94.4; // mm (9.44cm from top)
    const vacantAreaHeight = 22.8; // mm (2.28cm height)

    printWindow.document.write(`
  <!DOCTYPE html>
  <html>
  <head>
    <title>Print Card</title>
    <style>
      /* PRINT STYLES ONLY */
      @media print {
        @page {
          size: ${cardWidth}mm ${cardHeight}mm;
          margin: 0;
          padding: 0;
        }
        
        html, body {
          width: ${cardWidth}mm;
          height: ${cardHeight}mm;
          margin: 0;
          padding: 0;
          background: transparent;
        }
        
        .print-container {
          position: relative;
          width: ${cardWidth}mm;
          height: ${cardHeight}mm;
        }
        
        .print-area {
          position: absolute;
          top: ${vacantAreaTop}mm;
          left: 0;
          width: ${cardWidth}mm;
          height: ${vacantAreaHeight}mm;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 5mm;
          box-sizing: border-box;
        }
        
        .attendee-name {
          font-size: 12pt;
          font-weight: 900;
          color: #000000;
          margin: 0;
          padding: 0;
          text-transform: uppercase;
          letter-spacing: 0.2mm;
          line-height: 1;
          height: 8mm;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Arial Black', sans-serif;
          text-shadow: none;
          -webkit-text-stroke: 0;
        }
        
        .attendee-type {
          font-size: 7.5pt;
          font-weight: 700;
          color: #000000;
          margin: 0.5mm 0 0 0;
          padding: 0;
          text-transform: uppercase;
          letter-spacing: 0.1mm;
          line-height: 1;
          height: 6mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .attendee-branch {
          font-size: 7pt;
          font-weight: 600;
          color: #000000;
          margin: 0.5mm 0 0 0;
          padding: 0;
          text-transform: uppercase;
          letter-spacing: 0.1mm;
          line-height: 1;
          height: 5mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        /* HIDE EVERYTHING WHEN PRINTING */
        .no-print, .no-print * {
          display: none !important;
          visibility: hidden !important;
        }
      }
      
      /* SCREEN PREVIEW STYLES */
      @media screen {
        body {
          margin: 0;
          padding: 20px;
          background: #f0f0f0;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          min-height: 100vh;
        }
        
        .print-container {
          width: ${cardWidth}mm;
          height: ${cardHeight}mm;
          background: white;
          position: relative;
          border: 1px solid #ccc;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          transform: scale(0.8);
          transform-origin: top center;
        }
        
        .print-area {
          position: absolute;
          top: ${vacantAreaTop}mm;
          left: 0;
          width: ${cardWidth}mm;
          height: ${vacantAreaHeight}mm;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 5mm;
          box-sizing: border-box;
          border: 1px dashed red;
          background: rgba(255,0,0,0.05);
        }
        
        .attendee-name {
          font-size: 10.5pt;
          font-weight: 900;
          color: #000000;
          margin: 0;
          padding: 0;
          text-transform: uppercase;
          letter-spacing: 0.2mm;
          line-height: 1;
          height: 8mm;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Arial Black', sans-serif;
        }
        
        .attendee-type {
          font-size: 7.5pt;
          font-weight: 700;
          color: #000000;
          margin: 0.5mm 0 0 0;
          padding: 0;
          text-transform: uppercase;
          letter-spacing: 0.1mm;
          line-height: 1;
          height: 6mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .attendee-branch {
          font-size: 7pt;
          font-weight: 600;
          color: #000000;
          margin: 0.5mm 0 0 0;
          padding: 0;
          text-transform: uppercase;
          letter-spacing: 0.1mm;
          line-height: 1;
          height: 5mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .preview-guide {
          position: fixed;
          top: 10px;
          left: 10px;
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 10px;
          font-size: 12px;
          border-radius: 5px;
          z-index: 1000;
        }
      }
    </style>
  </head>
  <body>
    <!-- Preview guide - only shows on screen, hidden when printing -->
    <div class="no-print preview-guide">
      PREVIEW: Card ${cardWidth}mm × ${cardHeight}mm | Print area starts at ${vacantAreaTop}mm (${vacantAreaHeight}mm height)
    </div>
    
    <div class="print-container">
      <div class="print-area">
        <div class="attendee-name">${attendeeInfo.name || 'GUEST'}</div>
        <div class="attendee-type">${attendeeInfo.attendee_type || 'ATTENDEE'}</div>
        <div class="attendee-branch">${attendeeInfo.branch || 'MAIN'}</div>
      </div>
    </div>
    
    <script>
      window.onload = function() {
        // Auto print after a short delay
        setTimeout(function() {
          window.print();
        }, 500);
        
        // Close window after printing
        window.onafterprint = function() {
          window.close();
        };
      }
    </script>
  </body>
  </html>
  `);
    printWindow.document.close();
  };

  // Draggable Icon Logic
  const startDrag = (e) => {
    isDragging.current = true;
    isClick.current = true;
    dragOffset.current = {
      x: e.clientX - iconPosition.x,
      y: e.clientY - iconPosition.y,
    };
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  const onDrag = (e) => {
    if (!isDragging.current) return;
    isClick.current = false;
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    const boundedX = Math.max(0, Math.min(newX, window.innerWidth - 64));
    const boundedY = Math.max(64, Math.min(newY, window.innerHeight - 64));
    setIconPosition({ x: boundedX, y: boundedY });
  };

  const stopDrag = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", stopDrag);
  };

  const handleIconClick = async () => {
    if (!isClick.current) return;
    console.log("[handleIconClick] Icon clicked", { showModal });
    await fetchStatus();
    setShowModal(true);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
      {/* Embedded CSS for Animations */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-grow {
          animation: grow 0.6s ease-out;
        }
        .animate-pulse-slow {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes grow {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 100;
        }
        .draggable-icon {
          position: fixed;
          z-index: 40;
          cursor: pointer;
        }
        .draggable-icon:hover {
          transform: scale(1.05);
          transition: transform 0.2s ease-in-out;
        }
        .modal-content {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 101;
        }
        .draggable-icon:active {
          cursor: grabbing;
        }
        .scan-stage-indicator {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 20px;
          padding: 10px;
        }
        .stage-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 16px;
          transition: all 0.3s ease;
        }
        .stage-line {
          width: 60px;
          height: 4px;
          background-color: #e5e7eb;
          margin: 0 10px;
          transition: all 0.3s ease;
        }
        .stage-line.active {
          background-color: #38b2ac;
        }
      `}</style>

      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-4 shadow-lg fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-xl font-bold tracking-tight">Two-Step QR Scanner</div>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-base font-medium hover:bg-teal-700 transition-all duration-300 transform hover:scale-105 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="min-h-screen pt-20 p-6 lg:p-8 flex justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 animate-slide-in" style={{ marginTop: '70px' }}>

          {/* Scan Stage Indicator */}
          <div className="scan-stage-indicator">
            <div className={`stage-circle ${scanStage >= 1 ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-200 text-gray-500'}`}>
              1
            </div>
            <div className={`stage-line ${scanStage >= 2 ? 'active' : ''}`}></div>
            <div className={`stage-circle ${scanStage >= 2 ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-200 text-gray-500'}`}>
              2
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-teal-600 mb-6 text-center">
            {scanStage === 1 ? "First Scan - Identify Attendee" : "Second Scan - Verify Attendance"}
          </h2>
          <p className="text-base text-gray-600 mb-6 text-center">
            {scanStage === 1
              ? "Scan QR code to identify the attendee"
              : "Scan the same QR code again to confirm attendance"}
          </p>

          {/* Attendee Info Display */}
          {attendeeInfo && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg animate-slide-in">
              <h3 className="font-medium text-blue-700 mb-2">Attendee Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-medium">Name:</span> <strong className="text-lg">{attendeeInfo.name}</strong></div>
                <div><span className="font-medium">Type:</span> {attendeeInfo.attendee_type}</div>
                {attendeeInfo.branch && <div><span className="font-medium">Branch:</span> {attendeeInfo.branch}</div>}
                {attendeeInfo.email && <div><span className="font-medium">Email:</span> {attendeeInfo.email}</div>}
                {attendeeInfo.phone && <div><span className="font-medium">Phone:</span> {attendeeInfo.phone}</div>}
              </div>
            </div>
          )}

          {/* Camera Toggle Button */}
          {!qrData && !isVerified && (
            <button
              onClick={() => setIsCameraOpen(!isCameraOpen)}
              className={`w-full mb-6 px-4 py-3 rounded-lg font-medium text-white transition-all flex items-center justify-center space-x-2 ${isCameraOpen ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-500 hover:bg-teal-600'
                } animate-grow`}
            >
              {isCameraOpen ? (
                <>
                  <svg className="w-5 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Close Scanner</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{scanStage === 1 ? "Open First Scanner" : "Open Verification Scanner"}</span>
                </>
              )}
            </button>
          )}

          {/* QR Scanner */}
          {isCameraOpen && !qrData && !isVerified && (
            <div className="relative w-full aspect-square border-2 border-gray-200 rounded-lg overflow-hidden mb-6 animate-grow">
              <QrReader
                constraints={cameraConstraints}
                onResult={(result, error) => {
                  if (error) console.error("[QrReader] Scan Error:", error.message);
                  if (result) handleScanResult(result);
                }}
                containerStyle={{ width: "100%", height: "100%" }}
                videoStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-3/4 h-3/4">
                  <div className="absolute inset-0 border-4 border-teal-400 rounded-lg" />
                  <div className="absolute top-0 left-0 right-0 h-2 bg-teal-500 animate-pulse-slow" />
                </div>
              </div>
            </div>
          )}

          {/* Scanned Result and Verification */}
          {qrData && !isVerified && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-slide-in">
              <h3 className="text-base font-medium text-gray-700 mb-2">
                {scanStage === 1 ? "First Scan Code:" : "Verification Code:"}
              </h3>
              <div className="bg-white p-3 rounded text border-gray-200 border mb-4 overflow-x-auto">
                <p className="text-sm font-mono text-gray-800 break-all">{qrData}</p>
              </div>

              {scanStage === 1 ? (
                <button
                  onClick={handleVerify}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing First Scan...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Identify Attendee</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleVerify}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Verify Attendance</span>
                    </>
                  )}
                </button>
              )}

              <button
                onClick={handleNewScan}
                className="w-full mt-3 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all"
              >
                Cancel & Scan New
              </button>
            </div>
          )}

          {/* Verification Complete Section */}
          {isVerified && ticketData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 animate-slide-in">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-700">Attendance Verified!</h3>
                  <p className="text-sm text-green-600">Card ready for printing</p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-white rounded border">
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div><span className="font-medium">Attendee:</span> <strong>{ticketData.attendee_name}</strong></div>
                  <div><span className="font-medium">Type:</span> {ticketData.attendee_type}</div>
                  {attendeeInfo.branch && <div><span className="font-medium">Branch:</span> {attendeeInfo.branch}</div>}
                  <div><span className="font-medium">Event:</span> {ticketData.meeting_purpose}</div>
                  <div><span className="font-medium">Time:</span> {ticketData.verification_time}</div>
                  <div><span className="font-medium">Ticket ID:</span> {ticketData.ticket_id}</div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handlePrintTicket}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print on Card
                </button>
                <button
                  onClick={handleNewScan}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all"
                >
                  Scan Another
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Draggable Icon */}
      <div
        className="draggable-icon w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center shadow-lg"
        style={{ left: `${iconPosition.x}px`, top: `${iconPosition.y}px` }}
        onMouseDown={startDrag}
        onClick={handleIconClick}
      >
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      </div>

      {/* Meeting Data Modal */}
      {showModal && (
        <div className="modal-backdrop animate-fade-in" onClick={() => setShowModal(false)}>
          <div
            className="modal-content bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-teal-600">Meeting Report</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {meetingData ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Purpose</h4>
                  <p className="text-base text-gray-900">{meetingData.purpose}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Location</h4>
                  <p className="text-base text-gray-900">{meetingData.location}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Time</h4>
                  <p className="text-base text-gray-900">{new Date(meetingData.time).toLocaleString()}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Status</h4>
                  <p className="text-base text-gray-900 capitalize">{meetingData.status}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Description</h4>
                  <p className="text-base text-gray-900">{meetingData.description}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attendees</h4>
                  {meetingData.attendees.length === 0 ? (
                    <p className="text-sm text-gray-500">No attendees found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-teal-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Phone</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Role</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {meetingData.attendees.map((attendee) => (
                            <tr key={attendee.id} className="hover:bg-gray-50 transition-all">
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.name}
                              </td>
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.email}
                              </td>
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.phone}
                              </td>
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.role || "User"}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {attendee.attended ? (
                                  <span className="text-green-600">Attended</span>
                                ) : (
                                  <span className="text-red-600">Not Attended</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">There is no meeting data available</p>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="w-full py-6 text-center text-gray-500 text-sm bg-transparent mt-8">
        <a
          href="https://algo2botsinfotech.com/home/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-teal-600 transition-colors duration-300"
        >
          Powered by @ Algo2bots Infotech • {new Date().getFullYear()}
        </a>
      </footer>
    </div>
  );
}